import * as THREE from 'three';
import { DISTRICT } from '@somnhold/content';
import { DIRS, districtAt, districtCenter, hexLen, hexToWorld, isInside, type DistrictDef, type MapDef } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * The shape of a bastion's walls for drawing: which wall cells a curtain joins, which way each piece
 * faces and which district it closes. Works for any walled ground (the old ring plus its districts).
 *
 * The rule that keeps junctions clean: two neighbouring wall cells are joined only when they share a
 * neighbour inside the walls. Where a district wall meets the old ring, hex boundaries leave little
 * triangles of three wall cells; this joins two sides of the triangle (hugging the ground inside) and
 * never the third, so the wall turns once instead of doubling.
 */

const K = WORLD_PER_HEX / 1000;

export interface WallCell {
  q: number;
  r: number;
  key: string;
  /** World centre (Y = 0.04, the top of a wall tile). */
  p: THREE.Vector3;
  /** Unit vector on XZ pointing out of the walls (away from the ground it closes). */
  out: THREE.Vector2;
  /** Directions (indexes into DIRS) of the wall cells it is joined to. */
  links: number[];
  gate: boolean;
  /** The district this stretch of wall closes; undefined on the old bastion ring. */
  district: DistrictDef | undefined;
}

export interface WallLink {
  a: WallCell;
  b: WallCell;
  /** Unit vector on XZ pointing out of the walls, across the run. */
  out: THREE.Vector2;
}

export interface WallGraph {
  cells: Map<string, WallCell>;
  links: WallLink[];
}

export const cellWorld = (q: number, r: number, y = 0): THREE.Vector3 => {
  const c = hexToWorld(q, r);
  return new THREE.Vector3(c.x * K, y, c.y * K);
};

/**
 * Builds the graph of the wall cells `isWall` names (gates included: `isGate`), joined by the rule
 * above, for the walled ground of `map`.
 */
export const wallGraph = (map: MapDef, isWall: (q: number, r: number) => boolean, isGate: (q: number, r: number) => boolean): WallGraph => {
  const R = map.radius;
  const cells = new Map<string, WallCell>();
  const inside = (q: number, r: number): boolean => isInside(map, q, r);
  for (let r = -R; r <= R; r++) {
    for (let q = -R; q <= R; q++) {
      if (hexLen(q, r) > R || !isWall(q, r)) continue;
      const p = cellWorld(q, r, 0.04);
      const out = new THREE.Vector2();
      let district: DistrictDef | undefined;
      let ring = false;
      for (const d of DIRS) {
        if (!inside(q + d.q, r + d.r)) continue;
        const n = cellWorld(q + d.q, r + d.r);
        out.x += p.x - n.x;
        out.y += p.z - n.z;
        if (hexLen(q + d.q, r + d.r) < map.bastion) ring = true;
        else district ??= districtAt(map, q + d.q, r + d.r);
      }
      if (out.lengthSq() < 1e-6) out.set(p.x, p.z);
      out.normalize();
      cells.set(`${q},${r}`, { q, r, key: `${q},${r}`, p, out, links: [], gate: isGate(q, r), district: ring ? undefined : district });
    }
  }
  const links: WallLink[] = [];
  for (const a of cells.values()) {
    DIRS.forEach((d, i) => {
      const b = cells.get(`${a.q + d.q},${a.r + d.r}`);
      if (!b) return;
      // The two cells both of them touch.
      const c1 = DIRS[(i + 5) % 6] as (typeof DIRS)[number];
      const c2 = DIRS[(i + 1) % 6] as (typeof DIRS)[number];
      const in1 = inside(a.q + c1.q, a.r + c1.r);
      const in2 = inside(a.q + c2.q, a.r + c2.r);
      if (!in1 && !in2) return;
      a.links.push(i);
      // Each run once (from its first cell in row order).
      if (b.r < a.r || (b.r === a.r && b.q < a.q)) return;
      const out = new THREE.Vector2();
      for (const [c, ok] of [[c1, in1], [c2, in2]] as const) {
        if (!ok) continue;
        const n = cellWorld(a.q + c.q, a.r + c.r);
        out.x += (a.p.x + b.p.x) / 2 - n.x;
        out.y += (a.p.z + b.p.z) / 2 - n.z;
      }
      if (out.lengthSq() < 1e-6) out.copy(a.out).add(b.out);
      links.push({ a, b, out: out.normalize() });
    });
  }
  return { cells, links };
};

/** Yaw (about Y) that turns a model's +Z toward the XZ direction `d`. */
export const yawOf = (d: THREE.Vector2): number => Math.atan2(d.x, d.y);

/** Continuous hex distance from the keep, in hexes (cell centres land on integers). */
export const hexDistAt = (x: number, z: number): number => {
  const r = z / (WORLD_PER_HEX * 0.866);
  const q = x / WORLD_PER_HEX - r / 2;
  return Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
};

/**
 * Distance (in hexes) from a world XZ point to the walled ground, shifted so it reads like the old
 * `hexDistAt` around a plain ring: below `map.bastion` inside, `map.bastion` on the wall line, more
 * outside. Districts count as walled ground, so whatever kept clear of the ring keeps clear of them.
 */
export const walledDist = (map: MapDef): ((x: number, z: number) => number) => {
  const B = map.bastion;
  const centres = map.kind === 'bastion' ? (map.districts ?? []).map((d) => {
    const c = districtCenter(B, d.side);
    return cellWorld(c.q, c.r);
  }) : [];
  if (centres.length === 0) return hexDistAt;
  const off = B - DISTRICT.radius;
  return (x, z) => {
    let d = hexDistAt(x, z);
    for (const c of centres) d = Math.min(d, hexDistAt(x - c.x, z - c.z) + off);
    return d;
  };
};
